import React, { useEffect, useState, useRef } from 'react';
import { motion, useAnimation, useInView } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { ArrowDown, Recycle, Leaf, Wind, Droplets, Package, Factory, RefreshCw, Heart } from 'lucide-react';

export default function PiensaEnVerde() {
  const [activeTab, setActiveTab] = useState(1);
  const [counter1, setCounter1] = useState(0);
  const [counter2, setCounter2] = useState(0);
  const [counter3, setCounter3] = useState(0);
  const stats1Ref = useRef<HTMLDivElement>(null);
  const stats1InView = useInView(stats1Ref, { once: true });
  const controls = useAnimation();

  // Iniciar animación de contadores cuando la sección esté en vista
  useEffect(() => {
    if (stats1InView) {
      const interval1 = setInterval(() => {
        setCounter1(prev => {
          if (prev < 9900) {
            return prev + 250;
          }
          clearInterval(interval1);
          return 9900;
        });
      }, 30);

      const interval2 = setInterval(() => {
        setCounter2(prev => {
          if (prev < 10400) {
            return prev + 260;
          }
          clearInterval(interval2);
          return 10400;
        });
      }, 30);

      const interval3 = setInterval(() => {
        setCounter3(prev => {
          if (prev < 9000) {
            return prev + 225;
          }
          clearInterval(interval3);
          return 9000;
        });
      }, 30);

      return () => {
        clearInterval(interval1);
        clearInterval(interval2);
        clearInterval(interval3);
      };
    }
  }, [stats1InView]);

  useEffect(() => {
    if (stats1InView) {
      controls.start('visible');
    }
  }, [stats1InView, controls]);

  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const benefitItems = [
    { id: 1, icon: <Leaf className="h-8 w-8 text-[#FCAA0D]" />, title: "Conservación de recursos naturales" },
    { id: 2, icon: <Wind className="h-8 w-8 text-[#FCAA0D]" />, title: "Freno a la contaminación" },
    { id: 3, icon: <Package className="h-8 w-8 text-[#FCAA0D]" />, title: "Menos deshechos en vertederos" },
    { id: 4, icon: <Heart className="h-8 w-8 text-[#FCAA0D]" />, title: "Beneficio económico" },
    { id: 5, icon: <Factory className="h-8 w-8 text-[#FCAA0D]" />, title: "Menor consumo energético" },
  ];

  const phases = [
    {
      id: 1,
      title: "Retiramos",
      icon: <RefreshCw className="h-10 w-10" />,
      color: "from-[#FCAA0D] to-[#FCAA0D]/80",
      description: "las máquinas sin coste alguno para el cliente. Esto supone un triple beneficio, ya que se evita el tiempo invertido en la búsqueda de soluciones de reciclado, el posible coste de la retirada y además se obtiene liquidez económica con la que, de otra forma, no se contaba."
    },
    {
      id: 2,
      title: "Reparamos",
      icon: <Factory className="h-10 w-10" />,
      color: "from-[#FCAA0D] to-[#FCAA0D]/80",
      description: "los equipos en caso de que sea necesario. Bajo ninguna circunstancia ofreceríamos a nuestros clientes una máquina que no se haya revisado a conciencia, con el S.A.T. oficial de la marca si es necesario."
    },
    {
      id: 3,
      title: "Vendemos",
      icon: <Package className="h-10 w-10" />,
      color: "from-[#FCAA0D] to-[#FCAA0D]/80",
      description: "la maquinaria en un estado excelente, casi como recién salida de la fábrica. Y con un precio que está muy por debajo de su valor de mercado, en ocasiones llegando hasta un 60% de ahorro, y con entrega inmediata."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Piensa en Verde | Innotec Food Equipment</title>
        <meta name="description" content="Descubre el compromiso de Innotec con el medio ambiente a través de nuestra iniciativa de reutilización y reciclaje de maquinaria industrial alimentaria." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-[75vh] flex items-center">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0B] to-transparent z-10"></div>
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1586193151825-1547a90b4a45?ixlib=rb-4.0.3&auto=format&fit=crop&q=80&w=2070')] bg-cover bg-center bg-fixed z-0"></div>
          <video
            className="w-full h-full object-cover z-5 opacity-70"
            autoPlay
            muted
            loop
            playsInline
          >
            <source src="https://cdn.coverr.co/videos/coverr-close-up-of-recycling-symbol-on-wood-5261/1080p.mp4" type="video/mp4" />
          </video>
        </div>

        <div className="container mx-auto px-6 relative z-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-3 py-1 text-sm bg-[#FCAA0D] text-[#0A0A0B] rounded-full mb-6">
              Compromiso Medioambiental
            </span>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Piensa en <span className="text-[#FCAA0D]">Verde</span>
            </h1>
            <p className="text-xl text-gray-200 mb-8">
              En Innotec no solo innovamos en tecnología alimentaria, también lideramos en sostenibilidad dando una segunda vida a la maquinaria industrial.
            </p>
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
              <a
                href="#conocemas"
                className="bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-[#0A0A0B] font-medium py-3 px-6 rounded-lg transition-all transform hover:scale-105 flex items-center justify-center shadow-[0_0_15px_rgba(252,170,13,0.3)]"
              >
                Conoce más
                <ArrowDown className="ml-2 h-5 w-5" />
              </a>
              <a
                href="/used"
                className="bg-white/5 border border-white/20 text-white hover:border-[#FCAA0D] hover:shadow-[0_0_15px_rgba(252,170,13,0.2)] font-medium py-3 px-6 rounded-lg transition-all"
              >
                Ver maquinaria reciclada
              </a>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-10 left-0 right-0 flex justify-center animate-bounce">
          <a href="#intro" aria-label="Desplázate hacia abajo">
            <ArrowDown className="h-8 w-8 text-white" />
          </a>
        </div>
      </section>

      {/* Introducción */}
      <section id="intro" className="py-20 bg-[#0A0A0B]">
        <div className="container mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="flex justify-center mb-8">
              <div className="h-16 w-16 rounded-full bg-[#FCAA0D]/20 flex items-center justify-center">
                <Recycle className="h-8 w-8 text-[#FCAA0D]" />
              </div>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
              Innotec: compromiso con el <span className="text-[#FCAA0D]">medioambiente</span>
            </h2>
            <div className="prose prose-lg prose-invert max-w-none">
              <p className="text-white/70 mb-6">
                Por definición, la chatarra o txatarra proviene del euskera, y significa literalmente "lo viejo". Y así es como es tratada de manera habitual la maquinaria en desuso: se retiran las máquinas sobrantes de la industria, separando el material ferroso del resto de metales, y se traslada a las fundiciones donde le darán una segunda vida al metal.
              </p>
              <p className="text-white/70 mb-6">
                Esto evita la sobreexplotación minera, pero su coste para la salud del planeta sigue siendo elevado: las fundiciones emiten una cantidad significativa de dióxido de carbono (CO<sub>2</sub>) sobre todo por la combustión de carburantes durante la fusión de materiales. Y si hablamos de material no ferroso, la generación de residuos se dispara (zinc, plomo, cadmio, etc).
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Perjuicios medioambientales */}
      <section className="py-20 bg-gradient-to-b from-[#0A0A0B] to-[#0A0A0B]">
        <div className="container mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 text-center">
              Los perjuicios van más allá
            </h2>
            <p className="text-xl text-white/70 mb-12 text-center">
              El daño al medioambiente puede venir por muchos caminos
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              <motion.div
                whileHover={{ scale: 1.03 }}
                className="bg-white/5 border border-white/20 p-8 rounded-xl backdrop-blur-sm hover:shadow-[0_0_20px_rgba(252,170,13,0.1)] hover:border-[#FCAA0D]/20 transition-all"
              >
                <div className="flex items-center mb-4">
                  <div className="h-12 w-12 rounded-full bg-[#FCAA0D]/20 flex items-center justify-center mr-4">
                    <Wind className="h-6 w-6 text-[#FCAA0D]" />
                  </div>
                  <h3 className="text-xl font-bold text-white">Contaminación del aire</h3>
                </div>
                <p className="text-white/70">
                  Liberado en el propio proceso de fundido del metal, y de todos los materiales que libera la fusión: dióxido de azufre, fluoruro de hidrógeno, y otros gases contaminantes que contribuyen al cambio climático.
                </p>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.03 }}
                className="bg-white/5 border border-white/20 p-8 rounded-xl backdrop-blur-sm hover:shadow-[0_0_20px_rgba(252,170,13,0.1)] hover:border-[#FCAA0D]/20 transition-all"
              >
                <div className="flex items-center mb-4">
                  <div className="h-12 w-12 rounded-full bg-[#FCAA0D]/20 flex items-center justify-center mr-4">
                    <Droplets className="h-6 w-6 text-[#FCAA0D]" />
                  </div>
                  <h3 className="text-xl font-bold text-white">Contaminación del agua</h3>
                </div>
                <p className="text-white/70">
                  A través de residuos líquidos generados y desechados, en ocasiones, sin un correcto filtrado y depuración, por lo que acaban contaminando ríos, lagos y mares, afectando a ecosistemas acuáticos completos.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Beneficios */}
      <section id="conocemas" className="py-20 bg-gradient-to-b from-[#0A0A0B] to-[#0A0A0B]">
        <div className="container mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              <span className="text-[#FCAA0D]">5 beneficios fundamentales</span> de darle un nuevo uso a la maquinaria
            </h2>
            <p className="text-xl text-white/70 mb-12">
              La reutilización de maquinaria industrial genera impactos positivos en múltiples dimensiones
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 max-w-5xl mx-auto"
          >
            {benefitItems.map((item) => (
              <motion.div
                key={item.id}
                variants={fadeInUp}
                whileHover={{ y: -10, transition: { duration: 0.3 } }}
                className="bg-white/5 border border-white/20 rounded-lg p-6 flex flex-col items-center text-center backdrop-blur-sm hover:shadow-[0_0_20px_rgba(252,170,13,0.1)] hover:border-[#FCAA0D]/20 transition-all"
              >
                <div className="mb-4 p-3 rounded-full bg-[#FCAA0D]/10">
                  {item.icon}
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Estadísticas */}
      <section ref={stats1Ref} className="py-20 bg-[url('https://images.unsplash.com/photo-1581093588401-fbb62a02f120?ixlib=rb-4.0.3&auto=format&fit=crop&q=80&w=2070')] bg-cover bg-center bg-fixed relative">
        <div className="absolute inset-0 bg-[#0A0A0B]/70"></div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial="hidden"
            animate={controls}
            variants={fadeInUp}
            className="max-w-4xl mx-auto text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Nuestro <span className="text-[#FCAA0D]">impacto</span> en cifras
            </h2>
            <p className="text-xl text-white/70">
              Cada máquina recuperada es un paso hacia un futuro más sostenible
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={stats1InView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-[#0A0A0B]/80 backdrop-blur-sm p-8 rounded-xl text-center border border-white/20 hover:shadow-[0_0_20px_rgba(252,170,13,0.1)] hover:border-[#FCAA0D]/20 transition-all"
            >
              <div className="text-5xl font-bold text-[#FCAA0D] mb-2">{counter1.toLocaleString()}</div>
              <div className="text-xl text-white mb-4">Clientes ayudados</div>
              <p className="text-white/70">Empresas que han confiado en nuestro sistema de recuperación.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={stats1InView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-[#0A0A0B]/80 backdrop-blur-sm p-8 rounded-xl text-center border border-white/20 hover:shadow-[0_0_20px_rgba(252,170,13,0.1)] hover:border-[#FCAA0D]/20 transition-all"
            >
              <div className="text-5xl font-bold text-[#FCAA0D] mb-2">{counter2.toLocaleString()}</div>
              <div className="text-xl text-white mb-4">Toneladas recicladas</div>
              <p className="text-white/70">Maquinaria industrial recuperada y puesta de nuevo en circulación.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={stats1InView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="bg-[#0A0A0B]/80 backdrop-blur-sm p-8 rounded-xl text-center border border-white/20 hover:shadow-[0_0_20px_rgba(252,170,13,0.1)] hover:border-[#FCAA0D]/20 transition-all"
            >
              <div className="text-5xl font-bold text-[#FCAA0D] mb-2">{counter3.toLocaleString()}</div>
              <div className="text-xl text-white mb-4">Emisiones evitadas</div>
              <p className="text-white/70">Toneladas de CO₂ que no se han emitido gracias a la reutilización.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Sección B2B */}
      <section className="py-20 bg-[url('https://images.unsplash.com/photo-1551632436-cbf8dd35adfa?ixlib=rb-4.0.3&auto=format&fit=crop&q=80&w=2070')] bg-cover bg-center bg-fixed relative">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0B]/90 via-[#0A0A0B]/70 to-[#0A0A0B]/90"></div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center mb-16"
          >
            <div className="inline-block px-4 py-1 border-2 border-[#FCAA0D] rounded-full text-[#FCAA0D] text-lg font-medium mb-6">
              Nueva Modalidad
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Servicio de gestión <span className="text-[#FCAA0D]">B2B</span>
            </h2>
            <p className="text-xl text-white/80 mb-8">
              Presentamos la nueva modalidad de compra de maquinaria de alimentación de Innotec Food,
              que interconecta empresas en todo el mundo: el Servicio B2B.
            </p>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 p-6 rounded-xl mb-8">
              <p className="text-2xl font-medium text-white italic">
                "Lo que uno no utiliza, <span className="text-[#FCAA0D]">otro lo necesita</span>"
              </p>
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16"
          >
            <motion.div
              variants={fadeInUp}
              className="bg-[#0A0A0B]/80 backdrop-blur-sm border border-white/10 rounded-xl p-8 hover:shadow-[0_0_30px_rgba(252,170,13,0.2)] hover:border-[#FCAA0D]/30 transition-all duration-500"
            >
              <p className="text-lg text-white/80 leading-relaxed">
                Esta fórmula que INNOTEC pone a disposición de sus clientes, prácticamente sin costes añadidos,
                pretende poner en contacto a fabricantes que quieran vender su maquinaria porque no la están utilizando,
                con empresas que lo necesitan.
              </p>
              <p className="text-lg text-white/80 leading-relaxed mt-4">
                Innotec tendrá el único cometido de conectarlas entre sí. Ser el punto de encuentro para
                alcanzar el acuerdo comercial más beneficioso para ambos.
              </p>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="flex items-center justify-center"
            >
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&auto=format&fit=crop&q=80&w=1170"
                alt="Conectando empresas"
                className="rounded-xl shadow-lg max-h-[300px] object-cover"
              />
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-5xl mx-auto mb-16"
          >
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-8 text-center">
              ¿CÓMO <span className="text-[#FCAA0D]">FUNCIONA</span>?
            </h3>

            <div className="grid md:grid-cols-3 gap-6">
              <motion.div
                whileHover={{ y: -10 }}
                transition={{ duration: 0.3 }}
                className="bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm border border-white/10 rounded-xl p-6 flex flex-col items-center text-center hover:shadow-[0_0_20px_rgba(252,170,13,0.2)] hover:border-[#FCAA0D]/20 transition-all"
              >
                <div className="h-14 w-14 rounded-full bg-[#FCAA0D] flex items-center justify-center mb-4 text-[#0A0A0B] font-bold text-xl">
                  1
                </div>
                <p className="text-white/80">
                  Si eres una empresa y tienes maquinaria industrial alimentaria que ya no uses o preveas no usar,
                  sólo tienes que introducir los datos en el formulario. ¡Sé rápido, las plazas son limitadas!
                </p>
              </motion.div>

              <motion.div
                whileHover={{ y: -10 }}
                transition={{ duration: 0.3 }}
                className="bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm border border-white/10 rounded-xl p-6 flex flex-col items-center text-center hover:shadow-[0_0_20px_rgba(252,170,13,0.2)] hover:border-[#FCAA0D]/20 transition-all"
              >
                <div className="h-14 w-14 rounded-full bg-[#FCAA0D] flex items-center justify-center mb-4 text-[#0A0A0B] font-bold text-xl">
                  2
                </div>
                <p className="text-white/80">
                  Te solicitaremos datos para tener una visión perfecta de tu máquina. Ten en cuenta que el vendedor
                  puede estar al otro lado del mundo y necesitará detalles completos.
                </p>
              </motion.div>

              <motion.div
                whileHover={{ y: -10 }}
                transition={{ duration: 0.3 }}
                className="bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm border border-white/10 rounded-xl p-6 flex flex-col items-center text-center hover:shadow-[0_0_20px_rgba(252,170,13,0.2)] hover:border-[#FCAA0D]/20 transition-all"
              >
                <div className="h-14 w-14 rounded-full bg-[#FCAA0D] flex items-center justify-center mb-4 text-[#0A0A0B] font-bold text-xl">
                  3
                </div>
                <p className="text-white/80">
                  En el formulario deberás marcar el precio que quieres por tu equipo. El comprador podrá hacer una contraoferta,
                  pero la decisión final será exclusivamente del vendedor.
                </p>
              </motion.div>
            </div>
          </motion.div>

          <div className="max-w-5xl mx-auto mb-16">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-[#FCAA0D]/10 backdrop-blur-sm border border-[#FCAA0D]/30 rounded-xl p-8 hover:shadow-[0_0_30px_rgba(252,170,13,0.3)] transition-all"
            >
              <h3 className="text-2xl font-bold text-[#FCAA0D] mb-6 text-center">
                B2B <span className="text-white">ORO</span>
              </h3>
              <p className="text-white/80 text-lg mb-6">
                Para aquellas empresas que busquen un servicio integral tenemos a su disposición la modalidad B2B ORO de Innotec.
              </p>
              <p className="text-white/80 mb-6">
                Ahora no sólo te pondremos en contacto con potenciales compradores de todo el mundo, sino que también:
              </p>
              <ul className="space-y-3 mb-6">
                {[
                  "Recogeremos tu maquinaria",
                  "La custodiaremos en nuestras instalaciones",
                  "La limpiaremos en profundidad",
                  "El Servicio Técnico Oficial de la marca revisará y reparará todo lo necesario",
                  "Después de abrillantarla, haremos fotografías y vídeos profesionales",
                  "La publicaremos en nuestra web e informaremos a todos nuestros colaboradores"
                ].map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start"
                  >
                    <span className="text-[#FCAA0D] mr-2">✓</span>
                    <span className="text-white/80">{item}</span>
                  </motion.li>
                ))}
              </ul>
              <p className="text-xl text-white text-center font-medium">
                Y todo esto, <span className="text-[#FCAA0D]">¡sin costes adicionales al servicio!</span>
              </p>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center mb-16"
          >
            <h3 className="text-2xl font-bold text-white mb-6">
              No te quedes sin tu <span className="text-[#FCAA0D]">plaza</span>
            </h3>
            <p className="text-lg text-white/80 mb-8">
              El Servicio B2B Innotec es un servicio exclusivo para los clientes que lo soliciten dentro
              de un plazo estipulado. Es decir, funciona en modalidad de <span className="text-[#FCAA0D] font-medium">CUPOS</span>.
            </p>
            <p className="text-lg text-white/80 mb-8">
              Tras revisar toda la documentación que nos envíes sobre tus equipos y su situación,
              nuestro departamento encargado lo estudiará y revisará en profundidad para determinar
              si cumples los requisitos. Si es así, serás incluido en el programa exclusivo B2B Innotec.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col md:flex-row justify-center space-y-4 md:space-y-0 md:space-x-6"
          >
            <motion.a
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
              href="/comprador"
              className="bg-white px-8 py-4 rounded-lg text-[#0A0A0B] font-bold text-center shadow-lg flex items-center justify-center space-x-2 hover:shadow-[0_0_20px_rgba(255,255,255,0.4)] transition-all"
            >
              <span>Soy Comprador</span>
              <span className="text-sm bg-[#FCAA0D] px-2 py-1 rounded">Acceder al listado de stock</span>
            </motion.a>
            <motion.a
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
              href="/vendedor"
              className="bg-[#FCAA0D] px-8 py-4 rounded-lg text-[#0A0A0B] font-bold text-center shadow-lg flex items-center justify-center space-x-2 hover:shadow-[0_0_20px_rgba(252,170,13,0.4)] transition-all"
            >
              <span>Soy Vendedor</span>
              <span className="text-sm bg-white px-2 py-1 rounded">Cumplimentar formulario</span>
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* La solución INNOTEC */}
      <section className="py-20 bg-gradient-to-b from-[#0A0A0B] to-[#0A0A0B]">
        <div className="container mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            className="max-w-3xl mx-auto text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Y ahí es donde entra en juego <span className="text-[#FCAA0D]">INNOTEC</span>
            </h2>
            <p className="text-xl text-white/70">
              Somos el nexo de unión entre industrias alimentarias en lo que a reutilización de maquinaria se refiere. Por algo somos líderes en nuestro sector: tenemos un estricto sistema de <span className="text-white font-semibold">4 fases</span> que garantiza la confianza de nuestros clientes.
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <div className="mb-12">
              <div className="border-l-4 border-[#FCAA0D] pl-6 py-2">
                <h3 className="text-2xl font-bold text-white">
                  Todo el material necesario para la construcción de maquinaria nueva, y hablamos de toneladas, puede reutilizarse de otra forma:
                </h3>
                <p className="text-xl text-[#FCAA0D] mt-2">
                  Dándole una segunda vida a las máquinas en su conjunto, no sólo por su material.
                </p>
              </div>
            </div>

            {/* Pestañas de proceso */}
            <div className="mb-8">
              <div className="flex flex-wrap justify-center md:justify-start space-x-2 mb-8">
                {phases.map((phase) => (
                  <button
                    key={phase.id}
                    onClick={() => setActiveTab(phase.id)}
                    className={`px-6 py-3 rounded-full font-medium transition-all duration-300 mb-2 flex items-center ${
                      activeTab === phase.id
                        ? `bg-gradient-to-r ${phase.color} text-[#0A0A0B]`
                        : "bg-white/5 text-white/70 hover:bg-white/10"
                    }`}
                  >
                    <span className="mr-2">{phase.icon}</span>
                    {phase.title}
                  </button>
                ))}
              </div>

              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-white/5 border border-white/20 p-8 rounded-xl backdrop-blur-sm shadow-[0_0_20px_rgba(252,170,13,0.1)]"
              >
                <div className="flex flex-col md:flex-row md:items-start">
                  <div className="md:w-1/3 mb-6 md:mb-0 md:mr-8">
                    <div className={`rounded-xl overflow-hidden bg-gradient-to-br ${phases[activeTab-1].color} p-12 flex items-center justify-center`}>
                      <div className="text-[#0A0A0B] transform scale-150">
                        {phases[activeTab-1].icon}
                      </div>
                    </div>
                    <div className="mt-4 text-center md:text-left">
                      <h3 className="text-2xl font-bold text-white">
                        {phases[activeTab-1].title}
                      </h3>
                      <div className="h-1 w-16 bg-[#FCAA0D] mt-2 mx-auto md:mx-0"></div>
                    </div>
                  </div>
                  <div className="md:w-2/3">
                    <p className="text-white/70 text-lg leading-relaxed">
                      <span className="font-semibold text-[#FCAA0D]">Piensa en verde</span>
                      <br />
                      {phases[activeTab-1].description}
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-gradient-to-b from-[#0A0A0B] to-black relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?ixlib=rb-4.0.3&auto=format&fit=crop&q=80&w=2070')] bg-cover bg-center opacity-20"></div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-8 leading-tight">
              Con Innotec contribuirás al <span className="text-[#FCAA0D]">medioambiente</span>
            </h2>
            <p className="text-xl text-white/70 mb-10 max-w-3xl mx-auto">
              Y todo esto puedes hacerlo sin moverte de tu asiento, cómodamente desde tu ordenador, smartphone o tablet en nuestra web, que también está en continuo reciclaje para seguir innovando y mejorando la vida, no sólo de nuestros clientes sino del planeta.
            </p>
            <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/20 mb-12 max-w-2xl mx-auto shadow-[0_0_20px_rgba(252,170,13,0.1)]">
              <p className="text-2xl font-semibold text-white">
                No tengas duda. <span className="text-[#FCAA0D]">TODO</span> son ventajas en el uso de maquinaria reacondicionada.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-6 justify-center">
              <a
                href="/used"
                className="bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-[#0A0A0B] font-medium py-4 px-8 rounded-lg transition-all transform hover:scale-105 shadow-[0_0_15px_rgba(252,170,13,0.3)]"
              >
                Ver maquinaria disponible
              </a>
              <a
                href="/contacto"
                className="bg-white/5 border border-white/20 text-white hover:border-[#FCAA0D] hover:shadow-[0_0_15px_rgba(252,170,13,0.2)] font-medium py-4 px-8 rounded-lg transition-all"
              >
                Contactar para más información
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
